/** Building blocks used on staff screens: requests, documents and messages for a client. */
import { DraftReplyButton } from "@/components/ai-buttons";
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ask, askText } from "@/components/dialogs";
import { CalendarRange, CheckCircle2, Download, Eye, FileText, FolderOpen, Lock, MessageSquare, RotateCcw, Trash2, Upload, XCircle } from "lucide-react";
import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { Empty, Loading, Pager, type Paged, usePaged } from "@/components/page";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { api, errorMessage } from "@/lib/api";
import { formatBytes, formatDate, formatDateTime, localToday } from "@/lib/format";
import type { ClientRequest, Doc } from "@/lib/types";
import { useToast } from "@/lib/use-toast";
import { LockControl } from "./billing";
import { DocumentPreviewDialog } from "./document-viewer";
import { docMeta, YearFolders } from "./year-folders";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { FOLDER_GROUPS, STANDARD_FOLDERS, isPermanentFolder, latestOpenYear } from "@clientflow/shared";

const STATUS_STYLE: Record<string, string> = {
  open: "bg-amber-100 text-amber-900 dark:bg-amber-900/30 dark:text-amber-200",
  submitted: "bg-blue-100 text-blue-900 dark:bg-blue-900/30 dark:text-blue-200",
  accepted: "bg-emerald-100 text-emerald-900 dark:bg-emerald-900/30 dark:text-emerald-200",
  cancelled: "bg-muted text-muted-foreground",
};

const STATUS_LABEL: Record<string, string> = { open: "Awaiting client", submitted: "Submitted — review", accepted: "Accepted", cancelled: "Cancelled" };

export function RequestStatus({ status }: { status: string }) {
  return <span className={`whitespace-nowrap rounded-full px-2.5 py-0.5 text-xs font-medium ${STATUS_STYLE[status] ?? ""}`}>{STATUS_LABEL[status] ?? status}</span>;
}

const TYPE_LABEL: Record<string, string> = { document: "Document", question: "Question", approval: "Approval" };

export function RequestsPanel({ clientId, engagementId, canEdit }: { clientId: string; engagementId?: string; canEdit: boolean }) {
  const qc = useQueryClient();
  const { toast } = useToast();
  const key = ["/api/requests?clientId=", clientId];
  const { data, isLoading } = useQuery<ClientRequest[]>({ queryKey: key });
  const [title, setTitle] = useState("");
  const [type, setType] = useState<"document" | "question">("document");
  const create = useMutation({
    mutationFn: () => api.send("POST", "/api/requests", { clientId, engagementId, type, title }),
    onSuccess: () => {
      setTitle("");
      qc.invalidateQueries({ queryKey: key });
      toast({ title: "Request sent", description: "The request appears on the client's portal to-do list." });
    },
    onError: (e) => toast({ title: "Request not sent", description: errorMessage(e), variant: "destructive" }),
  });
  const review = useMutation({
    mutationFn: (v: { id: string; decision: "accept" | "reopen" | "cancel"; note?: string }) =>
      api.send("POST", `/api/requests/${v.id}/review`, { decision: v.decision, note: v.note }),
  });
  const rows = (data ?? []).filter((r) => !engagementId || r.engagementId === engagementId);
  const paged = usePaged(rows);

  return (
    <div className="space-y-4">
      {canEdit && (
        <form
          className="flex flex-col gap-2 sm:flex-row"
          onSubmit={(e) => {
            e.preventDefault();
            if (title.trim()) create.mutate();
          }}
        >
          <select aria-label="Request type" value={type} onChange={(e) => setType(e.target.value as "document")} className="h-9 rounded-md border border-input bg-background px-2 text-sm">
            <option value="document">Document</option>
            <option value="question">Question</option>
          </select>
          <Input aria-label="Requested item" placeholder="Describe the item requested, e.g. 2026 Form W-2 from your employer" value={title} onChange={(e) => setTitle(e.target.value)} />
          <Button type="submit" disabled={create.isPending || !title.trim()}>
            Send request
          </Button>
        </form>
      )}
      {isLoading ? (
        <Loading />
      ) : rows.length === 0 ? (
        <Empty>No outstanding requests.</Empty>
      ) : (
        <>
        <ul className="divide-y divide-border rounded-lg border border-border">
          {paged.items.map((r) => (
            <li key={r.id} className="flex flex-col gap-2 px-4 py-3 sm:flex-row sm:items-center">
              <div className="min-w-0 flex-1">
                <p className="text-sm font-medium">{r.title}</p>
                <p className="text-xs text-muted-foreground">
                  {TYPE_LABEL[r.type] ?? r.type} · Requested {formatDate(r.createdAt)}
                  {r.remindersSent > 0 && ` · Reminders sent: ${r.remindersSent}`}
                </p>
                {r.responseText && <p className="mt-1 rounded bg-muted px-2 py-1 text-sm">“{r.responseText}”</p>}
              </div>
              <RequestStatus status={r.status} />
              {canEdit && (
                <div className="flex gap-1">
                  {r.status === "submitted" && (
                    <>
                      <Button size="sm" variant="outline" onClick={() => review.mutate({ id: r.id, decision: "accept" })}>
                        <CheckCircle2 className="mr-1 h-4 w-4" /> Accept
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        aria-label="Return to client"
                        title="Return to client"
                        onClick={async () => {
                          const note = await askText({ title: "Return to client", label: "Reason for return (visible to the client)", required: true, confirmLabel: "Return to client" });
                          if (note !== null) review.mutate({ id: r.id, decision: "reopen", note });
                        }}
                      >
                        <RotateCcw className="h-4 w-4" />
                      </Button>
                    </>
                  )}
                  {(r.status === "open" || r.status === "submitted") && (
                    <Button size="sm" variant="ghost" aria-label="Cancel request" title="Cancel request" onClick={() => review.mutate({ id: r.id, decision: "cancel" })}>
                      <XCircle className="h-4 w-4" />
                    </Button>
                  )}
                </div>
              )}
            </li>
          ))}
        </ul>
        <Pager paged={paged} noun="requests" />
        </>
      )}
    </div>
  );
}

/** `onFiles` receives a multiple selection all at once; otherwise `onFile` is called for each file. */
export function UploadButton({ onFile, onFiles, label = "Upload", disabled, multiple }: { onFile?: (f: File) => void; onFiles?: (f: File[]) => void; label?: string; disabled?: boolean; multiple?: boolean }) {
  const ref = useRef<HTMLInputElement>(null);
  return (
    <>
      <input
        ref={ref}
        type="file"
        className="hidden"
        multiple={multiple}
        accept=".pdf,.jpg,.jpeg,.png,.heic,.heif,.csv,.txt,.xlsx,.xls,.docx,.doc"
        onChange={(e) => {
          const files = Array.from(e.target.files ?? []);
          if (onFiles) onFiles(files);
          else for (const f of files) onFile?.(f);
          e.target.value = "";
        }}
      />
      <Button type="button" variant="outline" disabled={disabled} onClick={() => ref.current?.click()}>
        <Upload className="mr-2 h-4 w-4" /> {label}
      </Button>
    </>
  );
}

export function DocumentsPanel({ clientId, canEdit, lockable }: { clientId: string; canEdit: boolean; lockable?: boolean }) {
  const qc = useQueryClient();
  const { toast } = useToast();
  const key = ["/api/documents?clientId=", clientId];
  const { data, isLoading } = useQuery<Doc[]>({ queryKey: key });
  const [internal, setInternal] = useState(false);
  const [moving, setMoving] = useState<Doc | null>(null);
  const [viewing, setViewing] = useState<Doc | null>(null);
  const combine = useMutation({
    mutationFn: (v: { files: File[]; name: string; year: number | null; folder: string }) =>
      api.uploadFiles("/api/documents/combine", { clientId, folder: v.folder, name: v.name, visibility: internal ? "internal" : "shared", ...(v.year ? { year: String(v.year) } : {}) }, v.files),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: key });
      toast({ title: "Photos combined into one document" });
    },
    onError: (e) => toast({ title: "Upload failed", description: errorMessage(e), variant: "destructive" }),
  });
  const upload = useMutation({
    mutationFn: (v: { file: File; year: number | null; folder: string }) =>
      api.upload("/api/documents", { clientId, folder: v.folder, visibility: internal ? "internal" : "shared", ...(v.year ? { year: String(v.year) } : {}) }, v.file),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: key });
      toast({ title: "Document uploaded" });
    },
    onError: (e) => toast({ title: "Upload failed", description: errorMessage(e), variant: "destructive" }),
  });
  const remove = useMutation({
    mutationFn: (id: string) => api.send("DELETE", `/api/documents/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: key }),
  });

  // The year folders and a page of documents fill more than a screen.
  if (isLoading) return <Loading tall />;
  return (
    <>
      <YearFolders
        docs={data ?? []}
        mode="staff"
        zipUrl={(y) => `/api/documents/zip?clientId=${clientId}&year=${y}`}
        onUpload={canEdit ? (file, year, folder) => upload.mutate({ file, year, folder }) : undefined}
        onCombine={canEdit ? (files, name, year, folder) => combine.mutate({ files, name, year, folder }) : undefined}
        uploading={upload.isPending || combine.isPending}
        uploadExtras={
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={internal} onChange={(e) => setInternal(e.target.checked)} /> Internal (staff only)
          </label>
        }
        meta={(d) => docMeta(d)}
        actions={(d) => (
          <>
            {d.visibility === "internal" && (
              <Badge variant="outline" className="mr-1 gap-1">
                <Lock className="h-3 w-3" /> Internal
              </Badge>
            )}
            {lockable && d.visibility !== "internal" && <LockControl doc={d} clientId={clientId} />}
            {canEdit && (
              <Button size="icon" variant="ghost" aria-label={`File ${d.filename} elsewhere`} title="Change year or folder" onClick={() => setMoving(d)}>
                <CalendarRange className="h-4 w-4" />
              </Button>
            )}
            <Button size="icon" variant="ghost" aria-label={`Preview ${d.filename}`} title="Preview" onClick={() => setViewing(d)}>
              <Eye className="h-4 w-4" />
            </Button>
            <a className="rounded p-2 hover:bg-muted" href={`/api/documents/${d.id}/download`} title="Download" aria-label={`Download ${d.filename}`}>
              <Download className="h-4 w-4" />
            </a>
            {canEdit && (
              <Button
                size="icon"
                variant="ghost"
                aria-label={`Delete ${d.filename}`}
                title="Delete"
                onClick={async () => {
                  if (await ask({ title: "Delete this document?", description: `"${d.filename}" will be moved to the trash.`, confirmLabel: "Delete", destructive: true })) remove.mutate(d.id);
                }}
              >
                <Trash2 className="h-4 w-4" />
              </Button>
            )}
          </>
        )}
      />
      {moving && <MoveDocument doc={moving} onClose={() => setMoving(null)} onDone={() => qc.invalidateQueries({ queryKey: key })} />}
      {viewing && <DocumentPreviewDialog doc={viewing} onClose={() => setViewing(null)} />}
    </>
  );
}

type ThreadSummary = { id: string; clientId: string; clientName: string; subject: string; lastMessageAt: string; unread: boolean };
type ThreadDetail = {
  thread: { id: string; subject: string; clientId: string };
  /** Oldest first: the newest page of the conversation, or the page before `?before=`. */
  messages: Array<{ id: string; body: string; internal?: boolean; createdAt: string; authorUserId: string | null }>;
  authors: Array<{ id: string; name: string }>;
  hasEarlier: boolean;
};

/** Files a document under another year or folder. */
function MoveDocument({ doc, onClose, onDone }: { doc: Doc; onClose: () => void; onDone: () => void }) {
  const latest = latestOpenYear(localToday());
  const [year, setYear] = useState(String(doc.year));
  const [folder, setFolder] = useState(doc.folder);
  const save = useMutation({
    meta: { inlineError: true },
    mutationFn: () =>
      api.send("PATCH", `/api/documents/${doc.id}`, {
        ...(year === "default" ? { year: null } : Number(year) !== doc.year ? { year: Number(year) } : {}),
        ...(folder !== doc.folder ? { folder } : {}),
      }),
    onSuccess: () => {
      onDone();
      onClose();
    },
  });
  const years: number[] = [];
  for (let y = Math.max(latest, doc.year); y >= Math.min(2000, doc.year); y--) years.push(y);
  const folders = [...new Set([...STANDARD_FOLDERS, doc.folder])];
  const permanent = isPermanentFolder(folder);
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>File elsewhere</DialogTitle>
          <DialogDescription>{doc.filename}</DialogDescription>
        </DialogHeader>
        <div className="grid gap-3 sm:grid-cols-2">
          <div className="space-y-1">
            <Label htmlFor="move-folder">Folder</Label>
            <select id="move-folder" className="h-9 w-full rounded-md border border-input bg-background px-2 text-sm" value={folder} onChange={(e) => setFolder(e.target.value)}>
              {FOLDER_GROUPS.map((g) => (
                <optgroup key={g.key} label={g.label}>
                  {g.folders.map((f) => (
                    <option key={f} value={f}>
                      {f}
                    </option>
                  ))}
                </optgroup>
              ))}
              {folders
                .filter((f) => !STANDARD_FOLDERS.includes(f))
                .map((f) => (
                  <option key={f} value={f}>
                    {f}
                  </option>
                ))}
            </select>
          </div>
          <div className="space-y-1">
            <Label htmlFor="move-year">Year</Label>
            <select id="move-year" disabled={permanent} className="h-9 w-full rounded-md border border-input bg-background px-2 text-sm disabled:opacity-60" value={year} onChange={(e) => setYear(e.target.value)}>
              {doc.movedToYear && <option value="default">Its default year (from its work)</option>}
              {years.map((y) => (
                <option key={y} value={y}>
                  {y}
                </option>
              ))}
            </select>
          </div>
        </div>
        <p className="text-xs text-muted-foreground">{permanent ? "Permanent folders are kept from year to year." : "Tax documents go in the year their return is filed: a 2026 W-2 belongs in 2027."}</p>
        {save.error && <p className="text-sm text-destructive">{errorMessage(save.error)}</p>}
        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button disabled={save.isPending || (year !== "default" && Number(year) === doc.year && folder === doc.folder)} onClick={() => save.mutate()}>
            Move
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function ThreadView({ id, portal, meId }: { id: string; portal?: boolean; meId?: string }) {
  const qc = useQueryClient();
  const base = portal ? "/api/portal/threads/" : "/api/threads/";
  // The newest messages first; "Show earlier messages" fetches the page before the oldest one shown.
  const { data: pages, isLoading, fetchNextPage, hasNextPage, isFetchingNextPage } = useInfiniteQuery({
    queryKey: [base, id, "messages"],
    queryFn: ({ pageParam }) => api.get<ThreadDetail>(`${base}${id}${pageParam ? `?before=${pageParam}` : ""}`),
    initialPageParam: "",
    getNextPageParam: (last) => (last.hasEarlier && last.messages[0] ? last.messages[0].id : undefined),
    refetchInterval: 20_000,
  });
  const data = pages?.pages[0];
  const messages = [...(pages?.pages ?? [])].reverse().flatMap((p) => p.messages);
  const authors = (pages?.pages ?? []).flatMap((p) => p.authors);
  // Opening a thread marks it read on the server; refresh unread dots and badges.
  useEffect(() => {
    if (data) {
      qc.invalidateQueries({
        predicate: (q) => {
          const k = String(q.queryKey[0]);
          return k === "/api/threads" || k.startsWith("/api/threads?") || k === "/api/portal/threads" || k.startsWith("/api/portal/threads?") || k === "/api/portal/home";
        },
      });
    }
  }, [data?.thread.id, data?.messages.length]); // eslint-disable-line react-hooks/exhaustive-deps
  // The pane stays scrolled to the newest message; loading earlier ones keeps the reader's place.
  const pane = useRef<HTMLDivElement>(null);
  const lastId = messages[messages.length - 1]?.id;
  const firstId = messages[0]?.id;
  const height = useRef(0);
  useLayoutEffect(() => {
    const el = pane.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [lastId]);
  useLayoutEffect(() => {
    const el = pane.current;
    if (el && height.current) el.scrollTop += el.scrollHeight - height.current;
    height.current = 0;
  }, [firstId]);
  const [body, setBody] = useState("");
  const [internal, setInternal] = useState(false);
  const post = useMutation({
    mutationFn: () => api.send("POST", `${base}${id}/messages`, portal ? { body } : { body, internal }),
    onSuccess: () => {
      setBody("");
      qc.invalidateQueries({ queryKey: [base, id] });
      qc.invalidateQueries({ predicate: (q) => String(q.queryKey[0]).startsWith(portal ? "/api/portal/threads?" : "/api/threads?") || q.queryKey[0] === (portal ? "/api/portal/threads" : "/api/threads") });
    },
  });
  if (isLoading || !data) return <Loading />;
  const name = (uid: string | null) => authors.find((a) => a.id === uid)?.name ?? "Unknown";
  return (
    <div className="flex h-full flex-col">
      <h3 className="mb-3 text-base font-semibold">{data.thread.subject}</h3>
      <div ref={pane} className="max-h-[60vh] min-h-[12rem] flex-1 space-y-3 overflow-y-auto pr-1" aria-live="polite">
        {hasNextPage && (
          <div className="text-center">
            <Button
              type="button"
              size="sm"
              variant="outline"
              disabled={isFetchingNextPage}
              onClick={() => {
                height.current = pane.current?.scrollHeight ?? 0;
                fetchNextPage();
              }}
            >
              {isFetchingNextPage ? "Loading…" : "Show earlier messages"}
            </Button>
          </div>
        )}
        {messages.map((m) => {
          const mine = m.authorUserId === meId;
          return (
            <div key={m.id} className={`flex ${mine ? "justify-end" : "justify-start"}`}>
              <div
                className={`max-w-[85%] rounded-xl px-4 py-2.5 text-sm ${
                  m.internal ? "border border-dashed border-accent bg-accent/10" : mine ? "bg-primary text-primary-foreground" : "bg-muted"
                }`}
              >
                <p className="mb-1 text-[11px] opacity-70">
                  {m.internal && "Internal note · "}
                  {name(m.authorUserId)} · {formatDateTime(m.createdAt)}
                </p>
                <p className="whitespace-pre-wrap">{m.body}</p>
              </div>
            </div>
          );
        })}
      </div>
      <form
        className="mt-4 space-y-2"
        onSubmit={(e) => {
          e.preventDefault();
          if (body.trim()) post.mutate();
        }}
      >
        <Textarea aria-label="Message" placeholder="Write a reply…" value={body} onChange={(e) => setBody(e.target.value)} rows={3} />
        <div className="flex flex-wrap items-center justify-between gap-2">
          {!portal ? (
            <label className="flex items-center gap-2 text-sm text-muted-foreground">
              <input type="checkbox" checked={internal} onChange={(e) => setInternal(e.target.checked)} /> Internal note (not visible to the client)
            </label>
          ) : (
            <span />
          )}
          <span className="flex gap-2">
            {!portal && <DraftReplyButton threadId={id} onDraft={setBody} />}
            <Button type="submit" disabled={post.isPending || !body.trim()}>
              Send
            </Button>
          </span>
        </div>
      </form>
    </div>
  );
}

export function ThreadList({
  threads,
  selected,
  onSelect,
  showClient,
  paged,
  empty = "No messages.",
}: {
  threads: Array<Pick<ThreadSummary, "id" | "subject" | "lastMessageAt"> & Partial<ThreadSummary>>;
  selected?: string;
  onSelect: (id: string) => void;
  showClient?: boolean;
  /** For a list the server sends a page at a time: shows Previous / Next under it. */
  paged?: Paged;
  empty?: string;
}) {
  if (!threads.length) return <Empty>{empty}</Empty>;
  return (
    <div className="overflow-hidden rounded-lg border border-card-border bg-card">
      <ul className="divide-y divide-border">
        {threads.map((t) => (
          <li key={t.id}>
            <button
              type="button"
              aria-current={selected === t.id ? "true" : undefined}
              onClick={() => onSelect(t.id)}
              className={`w-full px-4 py-3 text-left transition-colors focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-primary ${selected === t.id ? "bg-primary/10 shadow-[inset_3px_0_0_var(--color-primary)]" : "hover:bg-muted/60"}`}
            >
              <div className="flex items-center gap-2">
                {t.unread && (
                  <span className="h-2 w-2 shrink-0 rounded-full bg-primary">
                    <span className="sr-only">Unread</span>
                  </span>
                )}
                <p className={`flex-1 truncate text-sm ${t.unread ? "font-semibold" : ""}`}>{t.subject}</p>
                <span className="text-xs text-muted-foreground">{formatDate(t.lastMessageAt)}</span>
              </div>
              {showClient && t.clientName && <p className="text-xs text-muted-foreground">{t.clientName}</p>}
            </button>
          </li>
        ))}
      </ul>
      {paged && <Pager paged={paged} noun="conversations" compact className="px-4 pb-3" />}
    </div>
  );
}

/** The reading pane before a conversation is chosen: a quiet prompt centred in the pane. */
export function NoThreadSelected() {
  return (
    <div className="flex h-full min-h-[inherit] flex-col items-center justify-center gap-2 text-center text-sm text-muted-foreground">
      <MessageSquare className="h-6 w-6 opacity-50" aria-hidden />
      Select a conversation to read it.
    </div>
  );
}

export type { ThreadSummary };
